import { useLanguage } from '../contexts/LanguageContext';
import { PageUseCase } from '../../corelogic/usecases/PageUseCase';
import { ContentUseCase, ExtendablePage } from '../../corelogic/usecases/ContentUseCase';

const pageUseCase = new PageUseCase();
const contentUseCase = new ContentUseCase();

/**
 * The main navigation, in one place for the header, the drawer and the footer.
 *
 * Every entry takes its label from its own page file, so the menu is edited at
 * /admin whichever kind of page it points at - the pages built in code through
 * `getNavLabel`, the pages assembled in the CMS through their own `navLabel`.
 * The names below are what is left if an editor empties the field. Legal pages
 * are not here - they live in the footer.
 *
 * The header fits roughly eight entries before it starts to crowd on a laptop,
 * so a CMS page is only added when an editor ticks "show in menu".
 */
export const BUILT_IN_NAV_ITEMS = [
  { key: 'home', path: '/', page: 'home' },
  { key: 'organisation', path: '/organisation', page: 'organisation' },
  { key: 'mitmachen', path: '/mitmachen', page: 'mitmachen' },
  { key: 'instagram', path: '/instagram', page: 'socialMedia' },
  { key: 'podcast', path: '/podcast', page: 'podcast' },
] as const;

export interface NavItem {
  /** Stable React key; CMS entries are namespaced to avoid collisions. */
  key: string;
  path: string;
  label: string;
}

export const useNavItems = (): NavItem[] => {
  const { language, t } = useLanguage();

  return [
    // A page that is switched off is not in the menu either - a menu entry
    // that redirects to the start page is worse than no entry. The start page
    // has no such switch at /admin, so it is always published and always here.
    ...BUILT_IN_NAV_ITEMS.filter((item) =>
      contentUseCase.isPublished(item.page as ExtendablePage)
    ).map((item) => ({
      key: item.key,
      path: item.path,
      label: contentUseCase.getNavLabel(item.page as ExtendablePage, language) ?? t(`nav.${item.key}`),
    })),
    ...pageUseCase.getNavItems(language).map((item) => ({
      key: `cms:${item.path}`,
      path: item.path,
      label: item.label,
    })),
  ];
};
